import { groupBy, map, sumBy } from "lodash-es";
import type {
  GetFlightAvailabilityTokenAdditionalService,
  GetFlightAvailabilityTokenBaggageDetails,
  GetFlightAvailabilityTokenFlight,
  GetFlightAvailabilityTokenOfferItem,
} from "../types/codegen";

export function isBaggageService(
  service: GetFlightAvailabilityTokenAdditionalService,
) {
  return ["BG", "CY"].includes(service.service.categoryCode);
}

type TravellerBaggageInfo = Partial<Record<"BG" | "CY", string>>;
export type BaggageInfo = Record<string, Record<string, TravellerBaggageInfo>>;

type MappedNormalizedBaggage = {
  checked: GetFlightAvailabilityTokenBaggageDetails[];
  carryOn: GetFlightAvailabilityTokenBaggageDetails[];
};

type TwoWayFlightMappedBaggage = {
  departure: MappedNormalizedBaggage;
  arrival: MappedNormalizedBaggage;
};

export function mapNormalizedBaggage(
  flights?: GetFlightAvailabilityTokenFlight[],
  offerItems?: GetFlightAvailabilityTokenOfferItem[],
) {
  const emptyResult: TwoWayFlightMappedBaggage = {
    departure: { checked: [], carryOn: [] },
    arrival: { checked: [], carryOn: [] },
  };

  if (!flights || flights.length === 0 || !offerItems?.length)
    return emptyResult;

  const getStops = (flight?: GetFlightAvailabilityTokenFlight) =>
    flight?.segments
      ?.flatMap((s) => [s.departure?.airport?.code, s.arrival?.airport?.code])
      .filter(Boolean) ?? [];

  const stopsDeparture = Array.from(new Set(getStops(flights[0])));
  const stopsArrival = Array.from(new Set(getStops(flights[1])));

  const makeCopyWithPieces = (
    baggage: GetFlightAvailabilityTokenBaggageDetails,
    multiplier: number,
  ) =>
    ({
      ...baggage,
      pieces: baggage.pieces ? baggage.pieces * multiplier : baggage.pieces,
    }) satisfies GetFlightAvailabilityTokenBaggageDetails;

  const collected: TwoWayFlightMappedBaggage = {
    departure: {
      checked: [],
      carryOn: [],
    },
    arrival: {
      checked: [],
      carryOn: [],
    },
  };

  const originDep = stopsDeparture[0];
  const destDep = stopsDeparture[1];
  const originArr = stopsArrival[0];
  const destArr = stopsArrival[1];

  offerItems.forEach((offer) => {
    const normalized = offer.normalizedBaggage ?? [];
    const count = Math.max(1, Number(offer.count ?? 1));

    if (originDep && destDep) {
      const b = normalized.find(
        (n) => n.origin === originDep && n.destination === destDep,
      );
      if (b?.checked)
        collected.departure.checked.push(makeCopyWithPieces(b.checked, count));
      if (b?.carryOn)
        collected.departure.carryOn.push(makeCopyWithPieces(b.carryOn, count));
    }

    if (originArr && destArr) {
      const b = normalized.find(
        (n) => n.origin === originArr && n.destination === destArr,
      );
      if (b?.checked)
        collected.arrival.checked.push(makeCopyWithPieces(b.checked, count));
      if (b?.carryOn)
        collected.arrival.carryOn.push(makeCopyWithPieces(b.carryOn, count));
    }
  });

  const mergeByRaw = (items: GetFlightAvailabilityTokenBaggageDetails[]) =>
    map(groupBy(items, "raw"), (group) => ({
      ...group[0],
      pieces: sumBy(group, (g) => Number(g.pieces) || 0),
    })) satisfies GetFlightAvailabilityTokenBaggageDetails[];

  return {
    departure: {
      checked: mergeByRaw(collected.departure.checked),
      carryOn: mergeByRaw(collected.departure.carryOn),
    },
    arrival: {
      checked: mergeByRaw(collected.arrival.checked),
      carryOn: mergeByRaw(collected.arrival.carryOn),
    },
  };
}
